// src/pages/prodactions/production-summary/ProductionTable.js
import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Box
} from "@mui/material";

// Import CustomTableContainer
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Import formatters dari file utilitas
import { formatText } from "../../../utils/formatters-master";

// Daftar nama bulan dalam Bahasa Indonesia
export const bulanList = [
    { id: 1, nama: "Januari" },
    { id: 2, nama: "Februari" },
    { id: 3, nama: "Maret" },
    { id: 4, nama: "April" },
    { id: 5, nama: "Mei" },
    { id: 6, nama: "Juni" },
    { id: 7, nama: "Juli" },
    { id: 8, nama: "Agustus" },
    { id: 9, nama: "September" },
    { id: 10, nama: "Oktober" },
    { id: 11, nama: "November" },
    { id: 12, nama: "Desember" },
];

// Fungsi untuk format ribuan tanpa desimal
export const formatThousandsNoDecimal = (value) => {
    if (!value && value !== 0) return "-";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "-";

    const roundedValue = Math.round(numValue);
    return roundedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Fungsi untuk menghitung total per bulan
export const calculateMonthlyTotals = (productionSummary, activeMonths) => {
    const monthlyTotals = {
        rencana: {},
        aktual: {}
    };

    activeMonths.forEach(month => {
        monthlyTotals.rencana[month] = 0;
        monthlyTotals.aktual[month] = 0;
    });

    productionSummary.forEach(row => {
        activeMonths.forEach(month => {
            monthlyTotals.rencana[month] += row[`rencana_${month}`] || 0;
            monthlyTotals.aktual[month] += row[`aktual_${month}`] || 0;
        });
    });

    return monthlyTotals;
};

// Komponen Header Tabel
export const ProductionTableHeader = ({ activeMonths, selectedYear }) => {
    return (
        <TableHead>
            <TableRow className="tr">
                <TableCell rowSpan={2} className="ded-tch">Trade Sector</TableCell>
                <TableCell rowSpan={2} className="ded-tch">Classification</TableCell>
                <TableCell rowSpan={2} className="ded-tch">Product Specification</TableCell>
                {/* Kolom untuk setiap bulan */}
                {activeMonths.map(month => (
                    <TableCell key={month} colSpan={2} align="center" className="ded-tch">
                        {bulanList.find(b => b.id === month)?.nama} {selectedYear}
                    </TableCell>
                ))}
                {/* Kolom Grand Total - 2 kolom untuk Rencana dan Aktual */}
                <TableCell colSpan={2} align="center" className="ded-tch" sx={{ minWidth: 160 }}>
                    Grand Total
                </TableCell>
            </TableRow>
            <TableRow className="tr">
                {/* Sub-kolom Rencana dan Aktual untuk setiap bulan */}
                {activeMonths.map(month => (
                    <React.Fragment key={month}>
                        <TableCell className="ded-tc">Rencana</TableCell>
                        <TableCell className="ded-tc">Aktual</TableCell>
                    </React.Fragment>
                ))}
                {/* Sub-kolom Grand Total Rencana dan Aktual */}
                <TableCell className="ded-tc">Rencana</TableCell>
                <TableCell className="ded-tc">Aktual</TableCell>
            </TableRow>
        </TableHead>
    );
};

// Komponen Body Tabel
export const ProductionTableBody = ({ 
    productionSummary, 
    activeMonths, 
    mergeSettings,
    monthlyTotals
}) => {
    // Hitung grand total untuk setiap baris
    const calculateRowGrandTotals = (row) => {
        let totalRencana = 0;
        let totalAktual = 0;
        
        activeMonths.forEach(month => {
            totalRencana += row[`rencana_${month}`] || 0;
            totalAktual += row[`aktual_${month}`] || 0;
        });
        
        return { totalRencana, totalAktual };
    };

    return (
        <TableBody>
            {productionSummary.map((row, index) => {
                // Hitung apakah ini baris pertama dalam grup Trade Sector
                const isFirstInTradeSector = index === 0 || 
                    productionSummary[index - 1].tradeSector !== row.tradeSector;
                
                // Hitung apakah ini baris pertama dalam grup Classification (dalam Trade Sector yang sama)
                const isFirstInClassification = index === 0 || 
                    productionSummary[index - 1].tradeSector !== row.tradeSector ||
                    productionSummary[index - 1].classification !== row.classification;
                
                // Dapatkan rowspan untuk Trade Sector
                let tradeSectorRowSpan = 0;
                if (mergeSettings.mergeTradeSector && isFirstInTradeSector) {
                    for (let i = index; i < productionSummary.length; i++) {
                        if (productionSummary[i].tradeSector === row.tradeSector) {
                            tradeSectorRowSpan++;
                        } else {
                            break;
                        }
                    }
                }
                
                // Dapatkan rowspan untuk Classification
                let classificationRowSpan = 0;
                if (mergeSettings.mergeClassification && isFirstInClassification) {
                    for (let i = index; i < productionSummary.length; i++) {
                        if (productionSummary[i].tradeSector === row.tradeSector && 
                            productionSummary[i].classification === row.classification) {
                            classificationRowSpan++;
                        } else {
                            break;
                        }
                    }
                }

                const { totalRencana, totalAktual } = calculateRowGrandTotals(row);

                return (
                    <TableRow key={index} hover>
                        {/* Trade Sector Column - MERGE & CENTER */}
                        {mergeSettings.mergeTradeSector ? (
                            isFirstInTradeSector ? (
                                <TableCell 
                                    className="ded-tbtrtc" 
                                    rowSpan={tradeSectorRowSpan}
                                    sx={{ 
                                        padding: "8px 16px",
                                        verticalAlign: 'middle',
                                        fontWeight: 'bold',
                                        backgroundColor: 'rgba(33, 150, 243, 0.04)',
                                    }}
                                    align="center"
                                >
                                    {formatText(row.tradeSector)}
                                </TableCell>
                            ) : null
                        ) : (
                            <TableCell 
                                className="ded-tbtrtc" 
                                sx={{ 
                                    padding: "8px 16px",
                                    verticalAlign: 'middle',
                                }}
                                align="left"
                            >
                                {formatText(row.tradeSector)}
                            </TableCell>
                        )}
                        
                        {/* Classification Column - MERGE & CENTER */}
                        {mergeSettings.mergeClassification ? (
                            isFirstInClassification ? (
                                <TableCell 
                                    className="ded-tbtrtc" 
                                    rowSpan={classificationRowSpan}
                                    sx={{ 
                                        padding: "8px 16px",
                                        verticalAlign: 'middle',
                                        fontWeight: 'bold',
                                        backgroundColor: 'rgba(76, 175, 80, 0.04)',
                                    }}
                                    align="center"
                                >
                                    {formatText(row.classification)}
                                </TableCell>
                            ) : null
                        ) : (
                            <TableCell 
                                className="ded-tbtrtc" 
                                sx={{ 
                                    padding: "8px 16px",
                                    verticalAlign: 'middle',
                                }}
                                align="left"
                            >
                                {formatText(row.classification)}
                            </TableCell>
                        )}
                        
                        {/* Product Specification Column - TIDAK PERNAH DI-MERGE */}
                        <TableCell 
                            className="ded-tbtrtc" 
                            sx={{ 
                                padding: "8px 16px",
                                verticalAlign: 'middle',
                            }}
                            align="left"
                        >
                            {formatText(row.productSpecification)}
                        </TableCell>
                        
                        {/* Kolom untuk setiap bulan - Rencana dan Aktual */}
                        {activeMonths.map(month => (
                            <React.Fragment key={month}>
                                <TableCell 
                                    className="ded-tbtrtc" 
                                    sx={{ 
                                        padding: "8px 16px",
                                        verticalAlign: 'middle',
                                    }}
                                    align="right"
                                >
                                    {formatThousandsNoDecimal(row[`rencana_${month}`])}
                                </TableCell>
                                <TableCell 
                                    className="ded-tbtrtc" 
                                    sx={{ 
                                        padding: "8px 16px",
                                        verticalAlign: 'middle',
                                    }}
                                    align="right"
                                >
                                    {formatThousandsNoDecimal(row[`aktual_${month}`])}
                                </TableCell>
                            </React.Fragment>
                        ))}
                        
                        {/* Kolom Grand Total Rencana */}
                        <TableCell 
                            className="ded-tbtrtc" 
                            sx={{ 
                                padding: "8px 16px",
                                verticalAlign: 'middle',
                                fontWeight: 'bold',
                                backgroundColor: 'rgba(255, 193, 7, 0.04)',
                            }}
                            align="right"
                        >
                            {formatThousandsNoDecimal(totalRencana)}
                        </TableCell>
                        
                        {/* Kolom Grand Total Aktual */}
                        <TableCell 
                            className="ded-tbtrtc" 
                            sx={{ 
                                padding: "8px 16px",
                                verticalAlign: 'middle',
                                fontWeight: 'bold',
                                backgroundColor: 'rgba(76, 175, 80, 0.04)',
                            }}
                            align="right"
                        >
                            {formatThousandsNoDecimal(totalAktual)}
                        </TableCell>
                    </TableRow>
                );
            })}
            
            {/* Baris Grand Total */}
            {productionSummary.length > 0 && (
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell 
                        colSpan={3} 
                        sx={{ 
                            fontWeight: 'bold',
                            padding: "8px 16px",
                        }}
                        align="center"
                    >
                        GRAND TOTAL
                    </TableCell>
                    
                    {/* Total per bulan */}
                    {activeMonths.map(month => (
                        <React.Fragment key={month}>
                            <TableCell 
                                sx={{ 
                                    fontWeight: 'bold',
                                    padding: "8px 16px",
                                    backgroundColor: '#e3f2fd',
                                }}
                                align="right"
                            >
                                {formatThousandsNoDecimal(monthlyTotals.rencana[month])}
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    fontWeight: 'bold',
                                    padding: "8px 16px",
                                    backgroundColor: '#e8f5e8',
                                }}
                                align="right"
                            >
                                {formatThousandsNoDecimal(monthlyTotals.aktual[month])}
                            </TableCell>
                        </React.Fragment>
                    ))}
                    
                    {/* Grand Total Rencana dari semua bulan */}
                    <TableCell 
                        sx={{ 
                            fontWeight: 'bold',
                            padding: "8px 16px",
                            backgroundColor: '#fff3e0',
                        }}
                        align="right"
                    >
                        {formatThousandsNoDecimal(
                            Object.values(monthlyTotals.rencana).reduce((a, b) => a + b, 0)
                        )}
                    </TableCell>
                    
                    {/* Grand Total Aktual dari semua bulan */}
                    <TableCell 
                        sx={{ 
                            fontWeight: 'bold',
                            padding: "8px 16px",
                            backgroundColor: '#e8f5e8',
                        }}
                        align="right"
                    >
                        {formatThousandsNoDecimal(
                            Object.values(monthlyTotals.aktual).reduce((a, b) => a + b, 0)
                        )}
                    </TableCell>
                </TableRow>
            )}
        </TableBody>
    );
};

// Komponen Empty State
export const ProductionTableEmpty = ({ selectedYear, debugInfo }) => {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
                border: "1px dashed #e0e0e0",
                borderRadius: 1,
                flexDirection: "column",
                p: 3,
            }}
        >
            <Typography variant="body1" color="text.secondary" gutterBottom>
                Tidak ada data produksi untuk tahun {selectedYear}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Pastikan data Distribution Mapping dan Daily Production sudah tersedia.
            </Typography>
            <Box sx={{ mt: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1, width: "100%", maxWidth: 600 }}>
                <Typography variant="caption" component="div" color="text.secondary">
                    <strong>Debug Info:</strong>
                    <br />
                    • Trade Sectors: {debugInfo.tradeSectorsList}
                    <br />
                    • Classification Categories: {debugInfo.classificationCategoriesList}
                    <br />
                    • Product Specifications: {debugInfo.productSpecificationsList}
                    <br />
                    • Distribution Mapping: {debugInfo.distributionMapping}
                    <br />
                    • Daily Production (total): {debugInfo.dailyProductionData}
                    <br />
                    • Daily Production (tahun {selectedYear}): {debugInfo.filteredProduction}
                    <br />
                    • Active Months: {debugInfo.activeMonths}
                </Typography>
            </Box>
        </Box>
    );
};

// Komponen Utama Tabel Produksi
export const ProductionTable = ({ 
    productionSummary, 
    activeMonths, 
    selectedYear,
    mergeSettings,
    debugInfo = {}
}) => {
    // Hitung total per bulan
    const monthlyTotals = calculateMonthlyTotals(productionSummary, activeMonths);

    // Hitung jumlah kolom untuk colSpan
    const columnCount = 3 + (activeMonths.length * 2) + 2; // 3 kolom tetap + (bulan * 2) + 2 kolom grand total

    return (
        <CustomTableContainer
            sx={{
                maxHeight: 400,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
            }}
            hoverScrollbar={true}
            scrollbarHeight="8px"
            scrollbarWidth="8px"
            scrollbarThumbColor="#c1c1c1"
            scrollbarHoverColor="#a8a8a8"
        >
            <Table stickyHeader aria-label="production summary table" size="small">
                <ProductionTableHeader 
                    activeMonths={activeMonths} 
                    selectedYear={selectedYear} 
                />
                
                {productionSummary.length > 0 ? (
                    <ProductionTableBody 
                        productionSummary={productionSummary}
                        activeMonths={activeMonths}
                        mergeSettings={mergeSettings}
                        monthlyTotals={monthlyTotals}
                    />
                ) : (
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={columnCount} align="center" sx={{ py: 3 }}>
                                <ProductionTableEmpty 
                                    selectedYear={selectedYear} 
                                    debugInfo={debugInfo}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                )}
            </Table>
        </CustomTableContainer>
    );
};

export default ProductionTable;